import { useCallback, useEffect, useRef, useState } from 'react';
import { maintenanceService, type MaintenanceWindow } from 'src/services/maintenanceService';

const REFRESH_INTERVAL_MS = 60_000;

export interface MaintenanceStatus {
  window: MaintenanceWindow | null;
  loading: boolean;
  isActive: boolean;
  isUpcoming: boolean;
  refresh: () => Promise<void>;
}

export function useMaintenanceStatus(): MaintenanceStatus {
  const [window, setWindow] = useState<MaintenanceWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const current = await maintenanceService.getCurrent();
      if (!mountedRef.current) return;
      setWindow(current);
    } catch {
      if (mountedRef.current) setWindow(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();

    let unsubscribe: (() => void) | null = null;
    maintenanceService
      .subscribe(() => {
        refresh();
      })
      .then((fn) => {
        if (!mountedRef.current) {
          fn();
          return;
        }
        unsubscribe = fn;
      })
      .catch(() => {});

    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      if (unsubscribe) unsubscribe();
    };
  }, [refresh]);

  const now = Date.now();
  const start = window ? new Date(window.scheduled_start).getTime() : 0;
  const end = window ? new Date(window.scheduled_end).getTime() : 0;
  const isActive = !!window && window.is_active;
  const isUpcoming = !!window && !window.is_active && end > now && start > now;

  return { window, loading, isActive, isUpcoming, refresh };
}
